/**
 * ProtectedPdfButton - כפתור הורדת PDF מוגבל למשתמשים מחוברים
 * 
 * - משתמש מחובר: כפתור רגיל להורדה + שליחת נתון לשרת
 * - משתמש לא מחובר: כפתור "מוקפא" + מודל להתחברות
 */

import { useState, useEffect } from "react";
import { Download, Lock, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/formula-pdf`;

// Google Logo SVG Component
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const ProtectedPdfButton = ({ 
  variant = "default", // "default" | "navbar" | "navbar-mobile" | "icon-only"
  language = "he",
  className = "",
  children
}) => {
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // בדיקת מצב התחברות
  useEffect(() => {
    const checkAuth = async () => {
      // Use the same token key as the rest of the app
      const token = localStorage.getItem("mart_session_token");
      
      try {
        const headers = { "Content-Type": "application/json" };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers,
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      }
    };
    
    checkAuth();
    
    // Listen for auth changes
    window.addEventListener("storage", checkAuth);
    window.addEventListener("authChange", checkAuth);
    
    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("authChange", checkAuth);
    };
  }, []);

  // שליחת נתון הורדה לשרת
  const trackDownload = async () => {
    const token = localStorage.getItem("mart_session_token");
    if (!token) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/user/track-pdf-download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
    } catch (error) {
      console.error("Failed to track download:", error);
    }
  };

  // טיפול בלחיצה
  const handleClick = async (e) => {
    if (!user) {
      e.preventDefault();
      setShowLoginModal(true);
      return;
    }
    
    // משתמש מחובר - עקוב אחרי ההורדה
    setIsDownloading(true);
    await trackDownload();
    
    // פתח את ה-PDF בחלון חדש
    window.open(PDF_URL, "_blank");
    
    setTimeout(() => setIsDownloading(false), 1000);
  };

  // התחברות עם Google
  const handleGoogleLogin = () => {
    // שמור את הדף הנוכחי לחזרה אחרי ההתחברות
    localStorage.setItem("mart_auth_redirect", window.location.pathname);
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // סגנונות לפי variant
  const getButtonStyles = () => {
    const baseStyles = "transition-all duration-300 flex items-center justify-center";
    
    if (!user) {
      // כפתור "מוקפא" למשתמש לא מחובר
      switch (variant) {
        case "navbar":
          return `${baseStyles} gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E4DC]/50 text-[#9A9790] cursor-not-allowed border border-dashed border-[#D1CCC4]`;
        case "navbar-mobile":
          return `${baseStyles} flex-col gap-0.5 p-2 rounded-lg bg-[#E8E4DC]/30 text-[#9A9790] cursor-not-allowed`;
        case "icon-only":
          return `${baseStyles} p-2 rounded-lg bg-[#E8E4DC]/50 text-[#9A9790] cursor-not-allowed`;
        default:
          return `${baseStyles} gap-2 px-6 py-3 rounded-xl bg-[#E8E4DC]/50 text-[#9A9790] cursor-not-allowed border-2 border-dashed border-[#D1CCC4]`;
      }
    }
    
    // כפתור רגיל למשתמש מחובר
    switch (variant) {
      case "navbar":
        return `${baseStyles} gap-1.5 px-3 py-1.5 rounded-lg text-[#4A5D43] hover:bg-[#4A5D43]/10 ${className}`;
      case "navbar-mobile":
        return `${baseStyles} flex-col gap-0.5 p-2 rounded-lg bg-[#4A5D43]/10 text-white hover:bg-[#4A5D43]/20 ${className}`;
      case "icon-only":
        return `${baseStyles} p-2 rounded-lg text-[#4A5D43] hover:bg-[#4A5D43]/10 ${className}`;
      default:
        return `${baseStyles} gap-2 px-6 py-3 rounded-xl bg-[#C47F64] text-white hover:bg-[#b06d53] hover:shadow-lg hover:scale-[1.02] ${className}`;
    }
  };

  // תוכן הכפתור
  const renderButtonContent = () => {
    const IconComponent = user ? Download : Lock;
    const iconSize = variant === "navbar-mobile" ? "h-5 w-5" : "h-4 w-4";
    
    if (variant === "icon-only") {
      return <IconComponent className={iconSize} />;
    }
    
    if (variant === "navbar-mobile") {
      return (
        <>
          <IconComponent className={iconSize} />
          <span className="text-[9px] font-medium">PDF</span>
        </>
      );
    }
    
    if (variant === "navbar") {
      return (
        <>
          <IconComponent className={iconSize} />
          <span className="text-xs font-medium hidden sm:inline">
            {language === "he" ? "PDF" : "PDF"}
          </span>
        </>
      );
    }
    
    // Default variant
    return children || (
      <>
        <IconComponent className={iconSize} />
        <span>{language === "he" ? "הורדת PDF עם כל המידע" : "Download PDF with all info"}</span>
      </>
    );
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isDownloading}
        className={getButtonStyles()}
        title={!user ? (language === "he" ? "נדרשת התחברות" : "Login required") : ""}
        data-testid="download-pdf-btn"
      >
        {renderButtonContent()}
      </button>

      {/* Login Modal */}
      {showLoginModal && (
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
          onClick={() => setShowLoginModal(false)}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            style={{ animation: "fadeIn 0.2s ease-out" }}
          />
          
          {/* Modal */}
          <div 
            className="relative bg-white dark:bg-[#1a231a] rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}
          >
            {/* Header with gradient */}
            <div className="relative bg-gradient-to-br from-[#4A5D43] to-[#5D7356] px-6 pt-8 pb-12 text-center">
              {/* Close button */}
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-3 left-3 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              
              {/* Lock icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm mb-4">
                <Lock className="h-8 w-8 text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-1">
                {language === "he" ? "תוכן בלעדי" : "Exclusive Content"}
              </h3>
              <p className="text-white/80 text-sm">
                {language === "he" ? "הורדת ה-PDF זמינה למשתמשים רשומים" : "PDF download is available for registered users"}
              </p>
            </div>
            
            {/* Content */}
            <div className="px-6 py-6 -mt-6">
              <div className="bg-[#FAF8F5] dark:bg-[#121812] rounded-xl p-4 mb-4 border border-[#E8E4DC] dark:border-[#2a3a2a]">
                <p className="text-sm text-[#6B7266] dark:text-[#9A9790] text-center leading-relaxed">
                  {language === "he" 
                    ? "התחברו עם חשבון Google כדי לקבל גישה מלאה למתכון ולכל המידע הטכני בפורמט PDF."
                    : "Sign in with your Google account to get full access to the recipe and all technical info in PDF format."}
                </p>
              </div>
              
              {/* Google Login Button */}
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl bg-white border-2 border-[#E8E4DC] hover:border-[#4A5D43]/30 hover:shadow-md transition-all duration-300 group"
              >
                <GoogleLogo className="w-5 h-5" />
                <span className="font-medium text-[#2C3329] group-hover:text-[#4A5D43] transition-colors">
                  {language === "he" ? "התחברות עם Google" : "Sign in with Google"}
                </span>
              </button>
              
              {/* Benefits */}
              <div className="mt-4 pt-4 border-t border-[#E8E4DC] dark:border-[#2a3a2a]">
                <p className="text-xs text-center text-[#9A9790] mb-2">
                  {language === "he" ? "למה להירשם?" : "Why sign up?"}
                </p>
                <div className="flex justify-center gap-4 text-xs text-[#6B7266]">
                  <span className="flex items-center gap-1">
                    <span className="text-[#4A5D43]">✓</span>
                    {language === "he" ? "PDF מלא" : "Full PDF"}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-[#4A5D43]">✓</span>
                    {language === "he" ? "קהילה" : "Community"}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-[#4A5D43]">✓</span>
                    {language === "he" ? "הנחות" : "Discounts"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embedded animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export { PDF_URL };
